import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import type { Tables } from '$/database.types';

export type OrgCredits = {
  credits_remaining: number;
  credits_purchased: number;
};

type CreditsRow = Tables<'credits'>;

async function fetchOrgCredits(orgId: string): Promise<OrgCredits> {
  const { data, error } = await supabaseClient
    .from('credits')
    .select('credits_remaining, credits_purchased')
    .eq('org_id', orgId)
    .maybeSingle();

  if (error) throw new Error(error.message);

  const row = data as CreditsRow | null;

  return {
    credits_remaining: row?.credits_remaining ?? 0,
    credits_purchased: row?.credits_purchased ?? 0,
  };
}

const useOrgCredits = () => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id;

  const query = useQuery({
    queryKey: ['credits', orgId],
    queryFn: () => fetchOrgCredits(orgId!),
    enabled: !!orgId,
  });

  const creditsRemaining = query.data?.credits_remaining ?? 0;
  const hasCredits = creditsRemaining > 0;

  return {
    ...query,
    creditsRemaining,
    hasCredits,
  };
};

export default useOrgCredits;
